import React, { useRef, useEffect } from 'react';
import About from './sections/About';
import Pentesting from './sections/Pentesting';
import Education from './sections/Education';
import Projects from './sections/Projects';
import Contact from './sections/Contact';

const SECTION_KEYS = ['about', 'pentesting', 'education', 'projects', 'contact'];

const MainContent = ({ activeSection, onSwitchSection, language }) => {
  const sections = {
    about: <About language={language} />,
    pentesting: <Pentesting language={language} />,
    education: <Education language={language} />,
    projects: <Projects language={language} />,
    contact: <Contact language={language} />
  };

  const currentIndex = SECTION_KEYS.indexOf(activeSection);

  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const onSwitchSectionRef = useRef(onSwitchSection);
  onSwitchSectionRef.current = onSwitchSection;

  const isScrollingRef = useRef(false);
  const lastScrollTimeRef = useRef(0);
  const scrollEndTimerRef = useRef(null);

  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchStartTimeRef = useRef(0);

  const transitionDirectionRef = useRef('next');
  const sectionRefs = useRef({});

  useEffect(() => {
    const activeEl = sectionRefs.current[activeSection];
    if (activeEl) {
      const dir = transitionDirectionRef.current;
      requestAnimationFrame(() => {
        if (dir === 'next') {
          activeEl.scrollTop = 0;
        } else if (dir === 'prev') {
          activeEl.scrollTop = activeEl.scrollHeight;
        }
      });
    }
    // Default to 'next' so direct clicks on nav links start at top
    transitionDirectionRef.current = 'next';
  }, [activeSection]);

  useEffect(() => {
    const DELTA_THRESHOLD = 30;
    const COOLDOWN_MS = 650;

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) < DELTA_THRESHOLD) {
        return;
      }

      const now = Date.now();

      if (isScrollingRef.current || (now - lastScrollTimeRef.current < COOLDOWN_MS)) {
        clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = setTimeout(() => {
          isScrollingRef.current = false;
        }, 150);
        return;
      }

      isScrollingRef.current = true;
      lastScrollTimeRef.current = now;

      clearTimeout(scrollEndTimerRef.current);
      scrollEndTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);

      const currentIdx = currentIndexRef.current;
      if (e.deltaY > 0) {
        transitionDirectionRef.current = 'next';
        const nextIndex = (currentIdx + 1) % SECTION_KEYS.length;
        onSwitchSectionRef.current(SECTION_KEYS[nextIndex]);
      } else if (e.deltaY < 0) {
        transitionDirectionRef.current = 'prev';
        const prevIndex = (currentIdx - 1 + SECTION_KEYS.length) % SECTION_KEYS.length;
        onSwitchSectionRef.current(SECTION_KEYS[prevIndex]);
      }
    };

    const handleTouchStart = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      touchStartXRef.current = e.touches[0].clientX;
      touchStartYRef.current = e.touches[0].clientY;
      touchStartTimeRef.current = Date.now();
    };

    const handleTouchEnd = (e) => {
      if (!e.changedTouches || e.changedTouches.length === 0) return;
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartXRef.current;
      const deltaY = touchEndY - touchStartYRef.current;
      const duration = Date.now() - touchStartTimeRef.current;

      // Ignore long gestures or taps
      if (duration > 700) return;

      const now = Date.now();
      if (now - lastScrollTimeRef.current < COOLDOWN_MS) return;

      const currentIdx = currentIndexRef.current;

      // 1. Horizontal swipe detection (swipe left = next, swipe right = prev)
      if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
        lastScrollTimeRef.current = now;
        if (deltaX < 0) {
          transitionDirectionRef.current = 'next';
          const nextIndex = (currentIdx + 1) % SECTION_KEYS.length;
          onSwitchSectionRef.current(SECTION_KEYS[nextIndex]);
        } else {
          transitionDirectionRef.current = 'prev';
          const prevIndex = (currentIdx - 1 + SECTION_KEYS.length) % SECTION_KEYS.length;
          onSwitchSectionRef.current(SECTION_KEYS[prevIndex]);
        }
        return;
      }

      // 2. Vertical edge swipe detection (when at top or bottom boundaries of active card)
      if (Math.abs(deltaY) > 70 && Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
        const activeSectionEl = sectionRefs.current[SECTION_KEYS[currentIdx]];
        if (activeSectionEl) {
          const { scrollTop, scrollHeight, clientHeight } = activeSectionEl;
          const isAtTop = scrollTop <= 8;
          const isAtBottom = scrollHeight - scrollTop - clientHeight <= 8;

          if (deltaY < 0 && isAtBottom) {
            // Swiped up while at bottom of content -> next section
            lastScrollTimeRef.current = now;
            transitionDirectionRef.current = 'next';
            const nextIndex = (currentIdx + 1) % SECTION_KEYS.length;
            onSwitchSectionRef.current(SECTION_KEYS[nextIndex]);
          } else if (deltaY > 0 && isAtTop) {
            // Swiped down while at top of content -> prev section
            lastScrollTimeRef.current = now;
            transitionDirectionRef.current = 'prev';
            const prevIndex = (currentIdx - 1 + SECTION_KEYS.length) % SECTION_KEYS.length;
            onSwitchSectionRef.current(SECTION_KEYS[prevIndex]);
          }
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      clearTimeout(scrollEndTimerRef.current);
    };
  }, []);

  const handleNextSection = () => {
    transitionDirectionRef.current = 'next';
    const nextIndex = (currentIndex + 1) % SECTION_KEYS.length;
    onSwitchSection(SECTION_KEYS[nextIndex]);
  };

  return (
    <main className="content-area">
      {SECTION_KEYS.map((key) => (
        <div 
          key={key} 
          ref={(el) => (sectionRefs.current[key] = el)}
          className={`content-section ${activeSection === key ? 'active' : ''}`}
        >
          {sections[key]}
        </div>
      ))}
      <div className="scroll-arrow" onClick={handleNextSection}></div>
    </main>
  );
};

export default MainContent;